<script lang="ts" setup>
import { FormContext } from 'vee-validate'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'

import { ServerError, UnprocessableEntityError } from '@/api/results'
import { BasketStep } from '@/types/basket'

import BasketFirstStep from '@/components/Basket/basket-state/BasketFirstStep.vue'
import BasketSecondStep from '@/components/Basket/basket-state/BasketSecondStep.vue'

/* Объект для динамического рендера шагов корзины */
const BASKET_STEP = {
  FIRST: BasketFirstStep,
  SECOND: BasketSecondStep,
}
const AUTO_DELETED_GIFT = 'Убрали подарок, проверьте условия для применения'

const { $sentryCaptureException } = useNuxtApp()

const basketStore = useBasketStore()
const { showSnackbar } = useAppStore()

const basketStep = ref<BasketStep>(BasketStep.FIRST)
const basketObserver = ref<FormContext | null>(null)

const basketError = computed(() => basketStore.error)
const clientPacksCount = computed(() => basketStore.clientPacksCount)
const clientAdditivesCount = computed(() => basketStore.clientAdditivesCount)
const getCurrentStepComponent = computed(() => BASKET_STEP[basketStep.value])

watch(basketError, async (basketError) => {
  if (!basketError) return

  if (
    basketError instanceof UnprocessableEntityError &&
    // @ts-ignore
    basketError.data?.product_id
  ) {
    showSnackbar({
      // @ts-ignore
      message: basketError.data?.product_id[0],
      type: SnackbarType.MESSAGE,
    })

    await basketStore.fetchBasket()
  } else if (
    basketError instanceof UnprocessableEntityError &&
    // @ts-ignore
    basketError.data
  ) {
    if (!basketObserver.value) {
      return
    }

    setTimeout(() => {
      // @ts-ignore
      const id = Object.keys(basketError.data)?.[0]

      scrollToErrorBlock(id)
    }, 50)
  } else if (basketError instanceof UnprocessableEntityError) {
    showSnackbar({
      message: 'Что-то пошло не так :( Попробуйте позже',
      type: SnackbarType.MESSAGE,
    })

    $sentryCaptureException(basketError)
  } else if (basketError instanceof ServerError) {
    $sentryCaptureException(basketError)
  }
})

// Следим за подарками - если подарков после обновления стало меньше -> показываем снекбар
watch(
  () => basketStore.data.gifts,
  (newState, prevState) => {
    const newNoChoiceGifts = newState.filter(
      ({ should_choose }) => !should_choose,
    )
    const prevNoChoiceGifts = prevState.filter(
      ({ should_choose }) => !should_choose,
    )

    // Если убрали подарки без выбора
    if (newNoChoiceGifts.length < prevNoChoiceGifts.length) {
      showSnackbar({
        message: AUTO_DELETED_GIFT,
      })
    }

    const newChoiceGifts = newState.filter(({ should_choose }) => should_choose)
    const prevChoiceGifts = prevState.filter(
      ({ should_choose }) => should_choose,
    )
    // Если убрали подарки с выбором
    // Кейс, когда в массиве gifts > 1 массивов с выбором, но мы отображаем всегда только первый
    if (!newChoiceGifts.length && prevChoiceGifts.length) {
      showSnackbar({
        message: AUTO_DELETED_GIFT,
      })
    }
  },
)

const scrollToErrorBlock = (id?: string) => {
  // Если id передан - сразу скроллим до блока ошибки
  if (id) {
    scrollToBlock(`#${id}`)

    return
  }

  // Получаем ошибки формы
  const fieldErrors = basketObserver.value?.errors

  if (!fieldErrors?.length) return

  // Получаем ключи ошибок формы
  const errors = Object.keys(fieldErrors)

  // Берем первую ошибку
  id = errors[0].split('-')[0]

  // Скроллим до блока ошибки
  scrollToBlock(`#${id}`)
}

const goToStep = async (newStep: BasketStep) => {
  basketStep.value = newStep

  await nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
}
</script>

<template>
  <component
    :is="getCurrentStepComponent"
    :client-packs-count="clientPacksCount"
    :client-additives-count="clientAdditivesCount"
    @scroll-to-error-block="scrollToErrorBlock"
    @go-to-step="goToStep"
  />
</template>

<style lang="scss" scoped>
.basket__section {
  position: relative;
  display: grid;
  gap: 60px 0;
  max-width: 620px;
  margin: auto;
}
</style>
